import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState , useMemo , useEffect } from "react";

import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as RadixLabel from "@radix-ui/react-label";
import { Check } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AxiosInstance from "@/utils/AxiosInstance";
import {useLocation } from "react-router";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface PulseOximeterFlowProps {
  isOpen: boolean;
  onComplete: () => void;
}

const respiratoryOptions = [
  "Respiratory diagnosis",
  "Pulmonary diagnosis",
  "Monitor Oxygen Level",
];

const cardiacOptions = [
  "Cardiac diagnosis",
  "Endocrine diagnosis",
  "Monitor Heart Rate",
];
  
// for yes type 1
  const respiratoryQuestions1 = [
    {
      question: "Have you ever experienced a pulmonary emergency in the past?",
      type: 'yes-no',
      answer: "",
      details: ""
    },
    {
      question: "Is there anything unusual in your daily routine today that could explain your low oxygen levels?",
      type: 'yes-no',
      answer: "",
      details: ""
    },
    {
      question: "Did you recently perform any physical activity or exercise?",
      type: 'yes-no',
      answer: "",
      details: ""
    },
    {
      question: "How do you feel?",
      type: 'text',
      answer: "",
      details: "" // Not used for 'text' type, but keeps the object shape consistent
    }
  ];

  const respiratoryQuestions1PainOption = [
  {
    option: "Chest pain",
    selected: "" // This would be updated to 'yes' or 'no'
  },
  {
    option: "Shortness of breath",
    selected: ""
  },
  {
    option: "Heart palpitations",
    selected: ""
  },
  {
    option: "Difficulty breathing",
    selected: ""
  },
  {
    option: "Fainting",
    selected: ""
  },
  {
    option: "Confusion",
    selected: ""
  }
];

// for no type 1
const respiratoryQuestions2 = [
{
question: "Is there anything unusual in your daily routine today that could explain your low oxygen levels?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Do you have nail polish on?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Do you have artificial nails?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Are your fingers cold?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Have you taken any pulmonary medication for today?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Have you had any breathing treatments today?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Did you take your oxygen reading after activity?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Are you feeling any stress, anxiety, or pain?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Have you consumed any alcohol?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Have you been smoking?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "Are you on any new medications, including prescription, over-the-counter, or illicit drugs (diuretics. decongestants, steroids, stimulants)?",
type: 'yes-no',
answer: "",
details: ""
},
{
question: "How do you feel?",
type: 'text',
answer: "",
details: "" // Not used for 'text' type, but keeps the object shape consistent
}
];

const recommendationOptions = [
  "Healthy diet",
  "Daily exercise",
  "Weight loss",
  "Avoid smoking",
  "Reduce stress",
  "Take pulmonary medication as prescribed",
  "Monitor oxygen level daily",
  "Follow-up with your Pulmonary Doctor",
  "Referral to a Pulmonoligist",
  "Escalate to provider",
  "Take your oxygen reading first thing in the morning, before taking any medications. Re-check your oxygen again 1 hour after taking your daily medication.",
];

// Type 2 things 

const patientExperiencingOptions = [
  "Too Low <50 BPM?",
  "Low <60 (bradycardia)",
  "High >100 (tachycardia)",
  "Too High >120",
];

const cardiacQuestions1 = [
  {
    question: "Is there anything unusual in your daily routine today that could explain your bradycardia?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Have you taken any cardiac medication for today?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Are you an athlete, runner, or do you exercise often?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Are you feeling any stress, anxiety, or pain?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Have you consumed any alcohol?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Have you been smoking?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Are you on any new medications, including prescription, over-the-counter, or illicit drugs (diuretics. decongestants, steroids, stimulants)?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "How do you feel?",
    type: 'text',
    answer: "",
    details: ""
  }
]; 

const cardiacQuestions2 = [
  {
    question: "Have you ever experienced a cardiac emergency in the past?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Is there anything unusual in your daily routine today that could explain your low heart rate/high heart rate?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "Did you recently perform any physical activity or exercise?",
    type: 'yes-no',
    answer: "",
    details: ""
  },
  {
    question: "How do you feel?",
    type: 'text',
    answer: "",
    details: ""
  }
];

const cardiacRecommendationOptions = [
  "Healthy diet",
  "Decrease tyramine rich foods",
  "Track potassium levels (K+)",
  "Daily exercise",
  "Weight loss",
  "Reduce caffeine",
  "Reduce alcohol",
  "Reduce smoking",
  "Reduce stress",
  "Take cardiac medication as prescribed",
  "Monitor heart rate daily",
  "Follow-up with your Cardiologist",
  "Referral to a cardiologist",
  "Get an EKG",
  "Escalate to provider",
  "Take your pulse reading first thing in the morning, before taking any medications. Re-check your pulse again 2 hours after taking your daily medication.",
];

const cardiacSymptomsOptions = [
  {
    option: "Chest pain",
    selected: ""
  },
  {
    option: "Shortness of breath",
    selected: ""
  },
  {
    option: "Heart Palpitations",
    selected: ""
  },
  {
    option: "Fainting",
    selected: ""
  },
  {
    option: "Confusion",
    selected: ""
  }
];

const PulseOximeterFlow = ({ isOpen, onComplete }: PulseOximeterFlowProps) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState<any>({});

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [moveToNextPath, setMoveToNextPath] = useState<string | null>(null);

  
  // For moving steps front back

  const handleNext = () => {
       let path = isRespiratorySelected ? "repirateoryPath" : "cardiacPath";
       setDetails((prevDetails: any) => ({
         ...prevDetails,
         seletedPath: path,
         seletedPathOptions : selectedOptions,
       }));
       setMoveToNextPath(path);
  };

  console.log(details);

    // Handle Is dissabled
  // const isDissabled = (): boolean => {
  //   if (step === 0) {
  //     return selectedOptions.length === 0;
  //   }

  //   if (isFirstFlow) {
  //     if (step === 1) return oxLevel === "";
  //     if (step === 2) return pulmonaryEmergency === "";
  //     if (step === 3) return unusalDailyRoutine === "";
  //     if (step === 4) return physicalActivity === "";
  //     if (step === 6) return !checkmank;
  //   } else {
  //     if (step === 1) return patientExperiencing === "";
  //     // Add disable logic for other steps in the second flow here
  //   }
    
  //   return false;
  // };

  // Option 1 :
  const handleOptionChange = (option: string) => {
    const newSelection = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option];
    setSelectedOptions(newSelection);
  };

  const isRespiratorySelected = selectedOptions.some((opt) =>
    respiratoryOptions.includes(opt),
  );
  const isCardiacSelected = selectedOptions.some((opt) =>
    cardiacOptions.includes(opt),
  );

  // Handle Enter key for Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !moveToNextPath && selectedOptions.length > 0 && !e.shiftKey) {
        const target = e.target as HTMLElement;
        // Don't trigger if user is typing in input/textarea
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleNext();
        }
      }
    };

    if (isOpen && !moveToNextPath) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, moveToNextPath, selectedOptions.length]);

  if (!isOpen) return null;

  return (
    <div className="space-y-6 mt-6">
      <Card>

       { !!moveToNextPath ? 
   moveToNextPath === "repirateoryPath" ? (
     <RespiratoryFlow details={details} setDetails={setDetails} setMoveToNextPath={setMoveToNextPath} onComplete={onComplete}/>
   ) : (
     <CardiacFlow details={details} setDetails={setDetails} setMoveToNextPath={setMoveToNextPath} onComplete={onComplete}/>
   )
        :(<>
        <CardHeader>
          <CardTitle>What is the main reason the patient utilizes the Pulse Oximeter?</CardTitle>
        </CardHeader>

        <CardContent>
              <div className="space-y-2">
                {respiratoryOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    {/* This is how you use the Radix Checkbox directly with styling */}
                    <RadixCheckbox.Root
                      id={option}
                      checked={selectedOptions.includes(option)}
                      onCheckedChange={() => handleOptionChange(option)}
                      disabled={isCardiacSelected}
                      className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                    >
                      <RadixCheckbox.Indicator className="flex items-center justify-center text-current">
                        <Check className="h-4 w-4" />
                      </RadixCheckbox.Indicator>
                    </RadixCheckbox.Root>
                    <RadixLabel.Root
                      htmlFor={option}
                      className={
                        isCardiacSelected
                          ? "text-muted-foreground"
                          : "font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      }
                    >
                      {option}
                    </RadixLabel.Root>
                  </div>
                ))}
                {cardiacOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <RadixCheckbox.Root
                      id={option}
                      checked={selectedOptions.includes(option)}
                      onCheckedChange={() => handleOptionChange(option)}
                      disabled={isRespiratorySelected}
                      className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                    >
                      <RadixCheckbox.Indicator className="flex items-center justify-center text-current">
                        <Check className="h-4 w-4" />
                      </RadixCheckbox.Indicator>
                    </RadixCheckbox.Root>
                    <RadixLabel.Root
                      htmlFor={option}
                      className={
                        isRespiratorySelected
                          ? "text-muted-foreground"
                          : "font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      }
                    >
                      {option}
                    </RadixLabel.Root>
                  </div>
                ))}
              </div>
        </CardContent>
        
    

        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => navigate("/alerts")}>
                Cancel
              </Button>
              <Button
                disabled={selectedOptions.length === 0}
                onClick={() => handleNext()}
                // className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        </CardContent>

        </>)}
        
       

        {/* <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Pulse oximeter flow will be implemented here.</p>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => navigate("/alerts")}>
                Cancel
              </Button>
              <Button onClick={onComplete} className="bg-orange-500 hover:bg-orange-600 text-white">
                Continue
              </Button>
            </div>
          </div>
        </CardContent> */}
      </Card>
    </div>
  );
};


// First Flow...
const RespiratoryFlow = ({details , setDetails, setMoveToNextPath , onComplete} : {details: any , setDetails: any , setMoveToNextPath: any , onComplete: any }) => {
  
  const [oxLevel, setOxLevel] = useState(details['An oxygen level of <90%?'] || "");
  const [toNextFlow, setToNextFlow] = useState<boolean>(false);

  // Handle Enter key for Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !toNextFlow && oxLevel !== "" && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleNext();
        }
      }
    };

    if (!toNextFlow) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [toNextFlow, oxLevel]);

  const handleBack = () => {
    setMoveToNextPath(null);
  };

  const handleNext = () => {
    if (oxLevel === "yes") {
       setDetails((prevDetails: any) => ({
         ...prevDetails,
         'An oxygen level of <90%?' : 'yes',
       }))

    } else if (oxLevel === "no") {
     
setDetails((prevDetails: any) => ({
         ...prevDetails,
         'An oxygen level of <90%?' : 'no',
       }))
    }

    setToNextFlow(true);
  };

  return (
    <>
    {toNextFlow ? (oxLevel === 'yes' ?  <LastFlow submisionType={'yes-type1'} questions={respiratoryQuestions1} details={details} setDetails={setDetails} handleBackState={() => setToNextFlow(false) } onComplete={onComplete} /> :  <LastFlow submisionType={'no-type1'} questions={respiratoryQuestions2} details={details} setDetails={setDetails} handleBackState={() => setToNextFlow(false) } onComplete={onComplete} />) : <><CardHeader>
          <CardTitle>{`An oxygen level of <90%?`}</CardTitle>
    </CardHeader>
    
    <YesNoComponent isSelected={oxLevel} setIsselected={setOxLevel} />
     
     <CardContent>
          <div className="space-y-4">
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => handleBack()}>
                Cancel
              </Button>
              <Button
                disabled={oxLevel === ""}
                onClick={() => handleNext()}
                // className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Confirm
              </Button>
            </div>
          </div>
        </CardContent></>}

    
    </>
  )
}


// Second Flow...
const CardiacFlow = ({details , setDetails, setMoveToNextPath , onComplete} : {details: any , setDetails: any , setMoveToNextPath: any , onComplete: any }) => {
  const [step, setStep] = useState(0);

  const handleFirstQuestionConfirm = () => {
    // If user says 'no' to the first question, we might end the flow or go to a different path.
    // For now, let's assume 'yes' proceeds and 'no' shows an alert and goes back.
    if (details['A heart rate <50 BPM?'] === 'yes') {
      setStep(1);
    } else {
      setStep(2);
    }
  };

  if (step === 0) {
    return (
      <RadioOptionsFlow
        title="Is the patient experiencing any of the following?"
        questionKey="patientExperiencing"
        options={patientExperiencingOptions}
        details={details}
        setDetails={setDetails}
        onBack={() => setStep(0)}
        onConfirm={() => {
          setStep(step + 1);
        }}

      />
    );
  }

  if (step === 1) {
    return (
      <SingleQuestionFlow
        title={'A heart rate <50 BPM?'}
        details={details}
        setDetails={setDetails}
        onBack={() => setMoveToNextPath(null)}
        onConfirm={handleFirstQuestionConfirm}
      />
    );
  }

  if(step === 2){
    return details['A heart rate <50 BPM?'] === 'yes' ?
     <LastFlow submisionType={'yes-type2'} questions={cardiacQuestions1} details={details} setDetails={setDetails} handleBackState={() => setStep(step-1)} onComplete={onComplete}/>
      :
      <LastFlow submisionType={'no-type2'} questions={cardiacQuestions2} details={details} setDetails={setDetails} handleBackState={() => setStep(step-1)} onComplete={onComplete}/>
    }


  return null; // Or a final summary component
}

const YesNoComponent = ({isSelected,setIsselected} : {isSelected: string; setIsselected: any}) => {
    
  return (
    <CardContent>
    <RadioGroup
                value={isSelected}
                onValueChange={setIsselected}
                className="space-x-3 flex"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="id-1" />
                  <Label htmlFor="id-1" className="cursor-pointer">
                    Yes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="id-2" />
                  <Label htmlFor="id-2" className="cursor-pointer">
                    No
                  </Label>
                </div>
              </RadioGroup>
    </CardContent>

  )
}

const LastFlow = ({ submisionType,questions, details, setDetails, handleBackState, onComplete}: { submisionType : string,questions: any, details: any, setDetails: any, handleBackState: any , onComplete: any }) => {

  const [ques, setQues] = useState([...questions]);
  const [step, setStep] = useState(0);

  console.log(ques);

  const currentQuestion = ques[step];

  // Generic handler to update a property of the current question
  const updateQuestion = (field: 'answer' | 'details', value: string) => {
    const newQues = [...ques];
    newQues[step] = { ...newQues[step], [field]: value };
    setQues(newQues);
  };

  // Handle Enter key for Next/Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          // Check if current question is answered
          const currentQ = ques[step];
          if (currentQ?.answer) {
            handleNext();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [step, ques]);

  const handleNext = () => {
    if (step < ques.length - 1) {
      setStep(step + 1);
    } else {
      // This is the last step, save the data and close
      let extraque = [];
      if(!!details?.['An oxygen level of <90%?']){
        extraque.push({ question : 'An oxygen level of <90%?' , answer : details?.['An oxygen level of <90%?']});
      }
      if(!!details?.['A heart rate <50 BPM?']){
        extraque.push({ question : 'A heart rate <50 BPM?' , answer : details?.['A heart rate <50 BPM?']});
      }
      setDetails({ ...details, respiratoryFollowUps: [...extraque,...ques] });
       console.log("Final Details:", { ...details, respiratoryFollowUps: [...ques,{ question : 'An oxygen level of <90%?' , answer : details?.['An oxygen level of <90%?']}] });
      //  return;
      setStep(step+1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      handleBackState(); // Go back to the previous main component
    }
  };

  return (
    step === ques.length ?
    (<>
    {submisionType === 'yes-type1' && <SymptomChecklist title='Are you currently experiencing any of the following?' painOptions={respiratoryQuestions1PainOption} details={details} setDetails={setDetails}  onBack={() => setStep(step-1)} onComplete={onComplete}  />}
    {submisionType === 'no-type1' &&  <MultiSelectChecklist
          title="Based on this assessment, I recommend the following: (select all that apply)"
          options={recommendationOptions}
          details={details}
          setDetails={setDetails}
          onBack={() => setStep(step - 1)}
          onComplete={onComplete}
        /> }
     {
      submisionType === 'yes-type2' && <MultiSelectChecklist
          title="Based on this assessment, I recommend the following: (select all that apply)"
          options={cardiacRecommendationOptions}
          details={details}
          setDetails={setDetails}
          onBack={() => setStep(step - 1)}
          onComplete={onComplete}
        />
     }   
     {
      submisionType === 'no-type2' && 
      <SymptomChecklist title='Are you currently experiencing any of the following?'
       painOptions={cardiacSymptomsOptions} details={details} setDetails={setDetails}  
       onBack={() => setStep(step-1)}
       onComplete={onComplete}
       />
     }
    </>)
    : (<>
      <CardHeader>
        <CardTitle>{currentQuestion.question}</CardTitle>
      </CardHeader>

      <>
        {currentQuestion.type === 'yes-no' && (
          <div className="space-y-4">
            <YesNoComponent
              isSelected={currentQuestion.answer}
              setIsselected={(val : string) => updateQuestion('answer', val)}
            />

            {currentQuestion.answer === 'yes' && (
               <CardContent>
              <div className="space-y-2 pt-4">
                <Label htmlFor="details-textarea">Please Explain:</Label>
                <Textarea
                  id="details-textarea"
                  placeholder="Provide details..."
                  value={currentQuestion.details}
                  onChange={(e) => updateQuestion('details', e.target.value)}
                  className="min-h-[120px]"
                />
              </div></CardContent>
            )}
          </div>
        )}

        {currentQuestion.type === 'text' && (
        <CardContent>
         <div className="space-y-2">
            <Label htmlFor="answer-textarea">Your Answer:</Label>
            <Textarea
              id="answer-textarea"
              placeholder="How do you feel?"
              value={currentQuestion.answer}
              onChange={(e) => updateQuestion('answer', e.target.value)}
              className="min-h-[120px]"
            />
          </div></CardContent>
        )}
      </>

      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
            <Button
              disabled={currentQuestion.answer === ""}
              onClick={handleNext}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {step === ques.length - 1 ? 'Next' : 'Next'}
            </Button>
          </div>
        </div>
      </CardContent>
    </>)
  );
};



// second page for type2
const RadioOptionsFlow = ({
  title,
  questionKey,
  options,
  details,
  setDetails,
  onBack,
  onConfirm,
}: {
  title: string;
  questionKey: string;
  options: string[];
  details: any;
  setDetails: any;
  onBack: () => void;
  onConfirm: () => void;
}) => {
  const [selectedValue, setSelectedValue] = useState(details[questionKey] || "");

  // Handle Enter key for Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && selectedValue !== "" && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleConfirmClick();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedValue]);

  const handleConfirmClick = () => {
    setDetails((prevDetails: any) => ({
      ...prevDetails,
      [questionKey]: selectedValue,
    }));
    onConfirm();
  };

  return (
    <>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      
      <CardContent>
        <RadioGroup
          value={selectedValue}
          onValueChange={setSelectedValue}
          className="space-y-3"
        >
          {options.map(option => (
            <div key={option} className="flex items-center space-x-2">
              <RadioGroupItem value={option} id={option.replace(/[^a-zA-Z0-9]/g, '')} />
              <Label htmlFor={option.replace(/[^a-zA-Z0-9]/g, '')} className="cursor-pointer">
                {option}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
      
      <CardContent>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={selectedValue === ""}
            // className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            Confirm
          </Button>
        </div>
      </CardContent>
    </>
  );
};



// third page for type2
const SingleQuestionFlow = ({
  title,
  details,
  setDetails,
  onBack,
  onConfirm,
}: {
  title: string;
  details: any;
  setDetails: any;
  onBack: () => void;
  onConfirm: () => void;
}) => {
  // Internal state for the yes/no selection, initialized from the main details object if a value exists
  const [selectedValue, setSelectedValue] = useState(details[title] || "");

  // Handle Enter key for Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && selectedValue !== "" && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleConfirmClick();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedValue]);

  const handleConfirmClick = () => {
    // Update the main details object with the selection for this question
    setDetails((prevDetails: any) => ({
      ...prevDetails,
      [title]: selectedValue,
    }));
    // Notify the parent component to proceed to the next step
    onConfirm();
  };

  return (
    <>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      
      <YesNoComponent isSelected={selectedValue} setIsselected={setSelectedValue} />
      
      <CardContent>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={selectedValue === ""}
            // className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            Confirm
          </Button>
        </div>
      </CardContent>
    </>
  );
};








// Used For last page Yes-Type1 / No-Type2
const SymptomChecklist = ({
  title,
  painOptions,
  details,
  setDetails,
  onBack,
  onComplete,
}: {
  title: string;
  painOptions: { option: string; selected: string }[];
  details: any;
  setDetails: any;
  onBack: () => void;
  onComplete: () => void;
}) => {
  const { state } = useLocation();
  const alert = state?.alert;
  const navigate = useNavigate();
  // Internal state for this component, initialized with the prop
  const [symptoms, setSymptoms] = useState(painOptions);
  const [isNotAnEmergency, setIsNotAnEmergency] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [showNursingNoteDialog, setShowNursingNoteDialog] = useState(false);
  
  console.log("details", details);
  // Handler to update a specific symptom in the array
  const handleSymptomChange = (symptomOption: string, value: string) => {
    setSymptoms(prevSymptoms =>
      prevSymptoms.map(item =>
        item.option === symptomOption ? { ...item, selected: value } : item
      )
    );
  };

  const handleConfirmClick = async () => {
     
     setDetails({ ...details, currenlyExperiencing : symptoms, isNotAnEmergency: isNotAnEmergency });
      let payload = { ...details, currenlyExperiencing : symptoms, isNotAnEmergency: isNotAnEmergency };
     try{
        setBtnLoading(true);
        let {data} : any = await AxiosInstance().post("/triage-forms/pulse-oximeter", {
              ...payload,
              alertId : alert?._id,
                            timestamp : new Date().toISOString(),

            });
            
            if(data?.success){
              toast.success(data?.message);
              // setShowNursingNoteDialog(true);
              onComplete();
            }
    
          }
          catch(e){
            toast.error('Something went wrong');
            console.log(e);
          }
          finally{
            setBtnLoading(false);
          }
  };

  const handleNursingNoteResponse = (response: "yes" | "no") => {
    setShowNursingNoteDialog(false);

    if (response === "yes") {
      // Navigate to patient profile
      const patientId = alert?.patientId?._id || alert?.patientId;
      if (patientId) {
        navigate(`/patients/${patientId}?tab=nursing-notes`);
      } else {
        // Fallback to alerts if patient ID not found
        navigate("/alerts");
      }
    } else {
      navigate("/alerts");
    }
  };

  // Check if all questions have been answered to enable the confirm button
  // "Not an emergency" checkbox is optional; only require symptom answers.
  const isConfirmDisabled = symptoms.some((item) => item.selected === "");

  // Handle Enter key for Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !isConfirmDisabled && !btnLoading && !showNursingNoteDialog && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleConfirmClick();
        }
      }
    };

    if (!showNursingNoteDialog) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isConfirmDisabled, btnLoading, showNursingNoteDialog]);

  // Handle Enter key for Nursing Note dialog
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     if (e.key === "Enter" && showNursingNoteDialog && !e.shiftKey) {
  //       e.preventDefault();
  //       // Default to "Yes" when Enter is pressed
  //       handleNursingNoteResponse("yes");
  //     }
  //   };

  //   if (showNursingNoteDialog) {
  //     window.addEventListener("keydown", handleKeyDown);
  //     return () => window.removeEventListener("keydown", handleKeyDown);
  //   }
  // }, [showNursingNoteDialog]);

  return (
    <>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-4 rounded-md border p-4">
            {symptoms.map((item) => (
              <div key={item.option} className="flex items-center justify-between">
                <Label className="font-semibold">{item.option}</Label>
                <YesNoComponent
                  isSelected={item.selected}
                  setIsselected={(val: string) => handleSymptomChange(item.option, val)}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center space-x-2 pt-4">
            <RadixCheckbox.Root
              id={'lastCheck'}
              checked={isNotAnEmergency}
              onCheckedChange={(checked) => setIsNotAnEmergency(Boolean(checked))}
              className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <RadixCheckbox.Indicator className="flex items-center justify-center text-current">
                <Check className="h-4 w-4" />
              </RadixCheckbox.Indicator>
            </RadixCheckbox.Root>
            <Label className="font-semibold" htmlFor="lastCheck">
              The patient is not experiencing any signs/symptoms of a pulmonary emergency.
            </Label>
          </div>
        </div>
      </CardContent>
      <CardContent>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={isConfirmDisabled || btnLoading}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {btnLoading ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </CardContent>

      {/* Nursing Note Alert Dialog */}
      {/* <Dialog open={showNursingNoteDialog} onOpenChange={setShowNursingNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              You have now completed your triage assessment. would you like to enter nursing note for this?
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => handleNursingNoteResponse("no")}
                className="border-red-500 text-red-500 hover:bg-red-50"
              >
                No
              </Button>
              <Button
                onClick={() => handleNursingNoteResponse("yes")}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Yes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog> */}
    </>
  );
};

// Used For last page Yes-Type2 / No-Type1
const MultiSelectChecklist = ({
  title,
  options,
  details,
  setDetails,
  onBack,
  onComplete,
}: {
  title: string;
  options: string[];
  details: any;
  setDetails: any;
  onBack: () => void;
  onComplete: () => void;
}) => {
  // Internal state to manage the selected options
  const [selected, setSelected] = useState<string[]>([]);
  const navigate = useNavigate();
  const { state } = useLocation();
  const alert = state?.alert;
  const [btnLoading, setBtnLoading] = useState(false);
  const [showNursingNoteDialog, setShowNursingNoteDialog] = useState(false);
  // Handler to add/remove an option from the selection
  const handleOptionChange = (option: string) => {
    setSelected(prev =>
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };

  // Handle Enter key for Confirm button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && selected.length > 0 && !btnLoading && !showNursingNoteDialog && !e.shiftKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          handleConfirmClick();
        }
      }
    };

    if (!showNursingNoteDialog) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [selected.length, btnLoading, showNursingNoteDialog]);

  // Handle Enter key for Nursing Note dialog
  // useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     if (e.key === "Enter" && showNursingNoteDialog && !e.shiftKey) {
  //       e.preventDefault();
  //       // Default to "Yes" when Enter is pressed
  //       handleNursingNoteResponse("yes");
  //     }
  //   };

  //   if (showNursingNoteDialog) {
  //     window.addEventListener("keydown", handleKeyDown);
  //     return () => window.removeEventListener("keydown", handleKeyDown);
  //   }
  // }, [showNursingNoteDialog]);

  const handleConfirmClick = async () => {
    // Update the main details object with the selected recommendations
    setDetails({ ...details, recommendations: selected });
    console.log("Final Details:", { ...details, recommendations: selected });
     let payload = { ...details, recommendations: selected };
     try{
        setBtnLoading(true);
        let {data} : any = await AxiosInstance().post("/triage-forms/pulse-oximeter", {
              ...payload,
              alertId : alert?._id,
              timestamp : new Date().toISOString(),
            });
            
            if(data?.success){
              toast.success(data?.message);
              // setShowNursingNoteDialog(true);
              onComplete();

            }
    
          }
          catch(e){
            toast.error('Something went wrong');
            console.log(e);
          }
          finally{
            setBtnLoading(false);
          }

  };

  // const handleNursingNoteResponse = (response: "yes" | "no") => {
  //   setShowNursingNoteDialog(false);

  //   if (response === "yes") {
  //     // Navigate to patient profile
  //     const patientId = alert?.patientId?._id || alert?.patientId;
  //     if (patientId) {
  //       navigate(`/patients/${patientId}?tab=nursing-notes`);
  //     } else {
  //       // Fallback to alerts if patient ID not found
  //       navigate("/alerts");
  //     }
  //   } else {
  //     navigate("/alerts");
  //   }
  // };

  return (
    <>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {options.map((option) => (
            <div key={option} className="flex items-start space-x-2">
              <RadixCheckbox.Root
                id={option}
                checked={selected.includes(option)}
                onCheckedChange={() => handleOptionChange(option)}
                className="peer mt-1 h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <RadixCheckbox.Indicator className="flex items-center justify-center text-current">
                  <Check className="h-4 w-4" />
                </RadixCheckbox.Indicator>
              </RadixCheckbox.Root>
              <RadixLabel.Root htmlFor={option} className="font-medium leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {option}
              </RadixLabel.Root>
            </div>
          ))}
        </div>
      </CardContent>
      <CardContent>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={selected.length === 0}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            Submit
          </Button>
        </div>
      </CardContent>

      {/* Nursing Note Alert Dialog */}
      {/* <Dialog open={showNursingNoteDialog} onOpenChange={setShowNursingNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              You have now completed your triage assessment. would you like to enter nursing note for this?
            </p>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => handleNursingNoteResponse("no")}
                className="border-red-500 text-red-500 hover:bg-red-50"
              >
                No
              </Button>
              <Button
                onClick={() => handleNursingNoteResponse("yes")}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Yes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog> */}
    </>
  );
};


export default PulseOximeterFlow;